import { useMemo } from "react"
import PropTypes from "prop-types"
import { identity, isNil, noop } from "lodash"
import { POSITIONS_COUNT } from "gatherers/constants"
import MultiLineOverlapping from "components/charts/multi-line-overlapping"
import BreakdownTooltip from "components/breakdown-tooltip"
import { autoFormatter, scaledPercentageFormatter } from "helpers/formatters/numbers"
import { useMultiLineHandlers } from "renderers/multi-line-graph/hooks/use-multiline-handlers"
import { GraphLoadingState, GraphEmptyState } from "renderers/multi-line-graph/components/graph-states"
import GraphErrorFallback from "renderers/multi-line-graph/components/graph-error-fallback"
import ChartNotice from "renderers/multi-line-graph/components/chart-notice"
import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { DATE } from "helpers/shapes"
import { ErrorBoundary } from "@sentry/react"

import "./styles.scss"

const EMPTY_ARRAY = []

// Post-dataTransform rows, not the gatherer contract — the legacy chart calls .toDate() on `date`.
const OVERLAPPING_ROW_SHAPE = PropTypes.shape({
  date: DATE.isRequired,
})

// MULTI_LINE_ITEM_SHAPE plus `area`, which only this chart's breakdown fill reads.
const OVERLAPPING_ITEM_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  area: PropTypes.string,
  disabled: PropTypes.bool,
  defaultDisabled: PropTypes.bool,
  category: PropTypes.string,
  type: PropTypes.oneOf(["overall", "factors", "category", "factor"]),
})

// The legacy overlapping chart hardcodes this key to split its left-hand total axis from the
// right-hand breakdown axis; callers emit their total series under it from the gatherer.
const TOTAL_SERIES_KEY = POSITIONS_COUNT

function MultiLineOverlappingGraphWithError({ status, errorMessage, ...props }) {
  if (status === ERROR) {
    throw new Error(errorMessage)
  }

  return <MultiLineOverlappingGraphComponent {...props} />
}

MultiLineOverlappingGraphWithError.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  errorMessage: PropTypes.string,
}

function MultiLineOverlappingGraphComponent({
  data,
  endDate,
  startDate,
  items = EMPTY_ARRAY,
  filterItems = identity,
  setItems = noop,
  setOnlyItem = noop,
  enableItems = noop,
  emptyMessage,
  noDataMessage,
  totalLabel,
  breakdownLabel,
  totalFormatter = autoFormatter,
  breakdownFormatter = scaledPercentageFormatter,
  tooltipFormatters,
  historicalOnlyMessage,
  isTruncated = false,
  hasFailedBatches = false,
  onDateClick = noop,
}) {
  const filteredItems = useMemo(() => filterItems(items, data), [filterItems, items, data])

  const {
    toggle,
    toggleOnly,
    focusChartLine,
    blurChartLine,
    isOnlyMode,
    focusedChartLine,
  } = useMultiLineHandlers(filteredItems, setItems, setOnlyItem, enableItems)

  if (!data || data.length === 0) {
    return <GraphEmptyState message={noDataMessage ?? emptyMessage} />
  }

  // An all-null breakdown or all-zero total renders an axis with nothing under it — the chart's own domain selectors .compact() a zero away.
  const hasBreakdownValue = filteredItems
    .filter(item => item.id !== TOTAL_SERIES_KEY)
    .some(item => data.some(point => !isNil(point[item.id])))
  const hasTotalValue = data.some(point => !isNil(point[TOTAL_SERIES_KEY]) && point[TOTAL_SERIES_KEY] !== 0)

  if (filteredItems.length === 0 || !hasBreakdownValue || !hasTotalValue) {
    return <GraphEmptyState message={noDataMessage ?? emptyMessage} />
  }

  return (
    <div className="graph-content">
      <ChartNotice isTruncated={isTruncated} hasFailedBatches={hasFailedBatches} historicalOnlyMessage={historicalOnlyMessage} />
      <div className="enhanced-multi-line-container">
        <MultiLineOverlapping
          data={data}
          items={filteredItems}
          startDate={startDate}
          endDate={endDate}
          setDate={onDateClick}
          toggle={toggle}
          toggleOnly={toggleOnly}
          onlyItem={isOnlyMode}
          focusChartLine={focusChartLine}
          blurChartLine={blurChartLine}
          focusedChartLine={focusedChartLine}
          totalLabel={totalLabel}
          breakdownLabel={breakdownLabel}
          totalTickFormat={totalFormatter}
          tickFormat={breakdownFormatter}
          tooltip={
            <BreakdownTooltip
              data={data}
              // BreakdownTooltip keeps disabled items; filtered here rather than in the shared component the risk panel also uses.
              items={filteredItems.filter(item => !item.disabled)}
              totalKey={TOTAL_SERIES_KEY}
              formatters={tooltipFormatters}
            />
          }
        />
      </div>
    </div>
  )
}

MultiLineOverlappingGraphComponent.propTypes = {
  data: PropTypes.arrayOf(OVERLAPPING_ROW_SHAPE),
  endDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(OVERLAPPING_ITEM_SHAPE).isRequired,
  filterItems: PropTypes.func,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  totalLabel: PropTypes.string,
  breakdownLabel: PropTypes.string,
  totalFormatter: PropTypes.func,
  breakdownFormatter: PropTypes.func,
  tooltipFormatters: PropTypes.objectOf(PropTypes.func),
  historicalOnlyMessage: PropTypes.string,
  isTruncated: PropTypes.bool,
  hasFailedBatches: PropTypes.bool,
  onDateClick: PropTypes.func,
}

export default function MultiLineOverlappingGraph({ status, loadingMessage = "", ...props }) {
  if (status === LOADING) {
    return <GraphLoadingState message={loadingMessage} />
  }

  return (
    <ErrorBoundary fallback={GraphErrorFallback}>
      {/* status must be forwarded: the inner component is what turns ERROR into a throw for this boundary. */}
      <MultiLineOverlappingGraphWithError status={status} {...props} />
    </ErrorBoundary>
  )
}

MultiLineOverlappingGraph.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  ...MultiLineOverlappingGraphComponent.propTypes,
  loadingMessage: PropTypes.string,
  errorMessage: PropTypes.string,
}
